import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { FiUserCheck } from "react-icons/fi";

import { acceptInvite, ApiCallError, lookupInvite } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LangSwitch from "@/components/LangSwitch";
import { useI18n } from "@/lib/i18n";

/**
 * Public page the invite link lands on. The token is checked first so an
 * expired or already-used link says so before the person picks a password.
 */
export default function AcceptInvitePage() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const nav = useNavigate();
  const [invite, setInvite] = useState<{ email: string; role: Role } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr(t("invite.noToken"));
      return;
    }
    lookupInvite(token).then(setInvite, (e) => setErr(e instanceof ApiCallError ? e.message : String(e)));
  }, [token, t]);

  if (user) return <Navigate to="/" replace />;

  const mismatch = pw2.length > 0 && pw !== pw2;

  return (
    <div className="flex h-full items-center justify-center bg-slate-950 p-4">
      <form
        onSubmit={async (ev) => {
          ev.preventDefault();
          setErr(null);
          setBusy(true);
          try {
            const r = await acceptInvite(token, pw);
            setUser({ id: 0, email: r.email, role: r.role });
            nav("/");
          } catch (e) {
            setErr(e instanceof ApiCallError ? e.message : String(e));
          } finally {
            setBusy(false);
          }
        }}
        className="w-full max-w-sm space-y-4 rounded-md border border-slate-800 bg-card p-4 md:p-6"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-base font-semibold">
            <FiUserCheck /> {t("invite.title")}
          </span>
          <LangSwitch />
        </div>
        {invite ? (
          <>
            <p className="text-sm text-slate-400">{t("invite.intro", { email: invite.email, role: invite.role })}</p>
            <div className="space-y-1">
              <label htmlFor="inv-pw" className="text-[11px] uppercase tracking-wide text-slate-400">
                {t("invite.password")}
              </label>
              <Input id="inv-pw" type="password" autoComplete="new-password" value={pw}
                     onChange={(e) => setPw(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="inv-pw2" className="text-[11px] uppercase tracking-wide text-slate-400">
                {t("invite.confirm")}
              </label>
              <Input id="inv-pw2" type="password" autoComplete="new-password" value={pw2}
                     onChange={(e) => setPw2(e.target.value)} />
            </div>
            {mismatch && <p className="text-sm text-signal-warn">{t("invite.mismatch")}</p>}
            <Button type="submit" className="w-full" disabled={busy || mismatch || pw.length < 8}>
              {t("invite.submit")}
            </Button>
            <p className="text-xs text-slate-500">{t("invite.pwHint")}</p>
          </>
        ) : (
          <p className="text-sm text-slate-400">{err ? "" : t("invite.checking")}</p>
        )}
        {err && <p className="text-sm text-signal-crit">{err}</p>}
      </form>
    </div>
  );
}
